import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import OutbreakRecordsTab    from '../../component/AnimalHealth/tabs/OutbreakRecordsTab';
import VaccinationRecordsTab from '../../component/AnimalHealth/tabs/VaccinationRecordsTab';
import MortalityRecordsTab   from '../../component/AnimalHealth/tabs/MortalityRecordsTab';
import ZoonoticAMRFlagsTab   from '../../component/AnimalHealth/tabs/ZoonoticAMRFlagsTab';
import UnconfiguredSubmodulePlaceholder from '../../component/shared/UnconfiguredSubmodulePlaceholder';
import { useModuleTabs } from '../../config/moduleCatalog';

const LOCAL_TABS = [
  { key: 'outbreak',    label: 'Outbreak Records'     },
  { key: 'vaccination', label: 'Vaccination Records'  },
  { key: 'mortality',   label: 'Mortality Records'    },
  { key: 'zoonotic',   label: 'Zoonotic & AMR Flags' },
];

const TAB_VIEWS = {
  outbreak:    OutbreakRecordsTab,
  vaccination: VaccinationRecordsTab,
  mortality:   MortalityRecordsTab,
  zoonotic:    ZoonoticAMRFlagsTab,
};

export default function AnimalHealth() {
  const navigate      = useNavigate();
  const { pathname }  = useLocation();
  const tabs          = useModuleTabs('animal-health', LOCAL_TABS);
  const segment       = pathname.split('/').filter(Boolean).pop();
  const activeTab     = tabs.some((tab) => tab.key === segment) ? segment : 'outbreak';
  const activeTabDef  = tabs.find((tab) => tab.key === activeTab);

  const [search,      setSearch]      = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const ActiveView = TAB_VIEWS[activeTab];

  function handleSearch() { setSearchQuery(search); }
  function handleKeyDown(e) { if (e.key === 'Enter') handleSearch(); }

  return (
    <div className="p-6 bg-white min-h-screen">

      {/* Page header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900 leading-none">
            Animal Health, Disease Surveillance &amp; One Health
          </h1>
          <a
            href="#"
            onClick={e => e.preventDefault()}
            className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 underline underline-offset-2 mt-1.5 hover:text-green-700"
          >
            Source from NADIS
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
        <span className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full mt-1">
          NADIS Integrated
        </span>
      </div>

      {/* Full-width search bar */}
      <div className="relative mb-5">
        <svg
          className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search here"
          className="w-full pl-11 pr-28 py-2.5 text-sm border border-gray-200 rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
        />
        <button
          onClick={handleSearch}
          className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-1.5 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
        >
          Search
        </button>
      </div>

      {/* Tab navigation */}
      <div className="flex items-center border-b border-gray-200 mb-5 gap-1">
        {tabs.map(tab => {
          const isActive = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              onClick={() => navigate(`/animal-health/${tab.key}`)}
              className={[
                'px-5 py-2.5 text-[13px] font-semibold whitespace-nowrap transition-colors border-b-2 -mb-px',
                isActive
                  ? 'border-green-600 text-green-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              ].join(' ')}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Active tab content */}
      {activeTabDef?.isCatalogOnly ? (
        <UnconfiguredSubmodulePlaceholder label={activeTabDef.label} />
      ) : (
        <ActiveView searchQuery={searchQuery} />
      )}

    </div>
  );
}
